import { DischemPharmacy } from '../models/dischem.model';
import { ComputAssistPharmacy } from '../models/computassist.model';
import { EasyRxPharmacy } from '../models/easyrx.model';

/**
 * Normalised pharmacy row rendered by the generic send dialog for every vendor.
 *
 * `secondary` is the descriptor-driven second line (Dischem/EasyRx → phone, ComputAssist → RAMS no.).
 * `raw` round-trips the concrete vendor record so `send()` / `buildSentPayload()` recover full data.
 */
export interface UnifiedPharmacy {
  id: string; // stable id + favourites key (siteNo / ramsno) — unique only WITHIN a vendor
  name: string;
  secondary?: { label: string; value: string };
  addressLine: string;
  province?: string;
  town?: string;
  isFavourite: boolean;
  /** Fields the generic client-side search matches against. */
  searchText: string;
  raw: any;
  /**
   * Owning integrator — stamped by the registry merge so the picker can show ALL vendors' pharmacies
   * in one list and the caller can resolve the routing descriptor via `registry.byId(vendorId)`.
   * The provider searches by name and need not know the vendor (it is an internal tag).
   */
  vendorId?: string;
  vendorLabel?: string;
}

function joinParts(parts: Array<string | undefined | null>): string {
  return parts
    .map(p => (p ?? '').toString().trim())
    .filter(Boolean)
    .join(', ');
}

export function dischemToUnified(p: DischemPharmacy): UnifiedPharmacy {
  const addressLine = joinParts([p.storeAddress, p.town, p.postalCode]);
  return {
    id: p.siteNo,
    name: p.storeName,
    secondary: p.telephoneNo ? { label: 'Tel', value: p.telephoneNo } : undefined,
    addressLine,
    province: p.province,
    town: p.town,
    isFavourite: !!p.isFavourite,
    searchText: joinParts([p.storeName, p.storeAddress, p.town, p.province, p.postalCode, p.telephoneNo, p.email]).toLowerCase(),
    raw: p,
  };
}

export function easyRxToUnified(p: EasyRxPharmacy): UnifiedPharmacy {
  const addressLine = joinParts([p.storeAddress, p.town, p.postalCode]);
  // practiceNo (the BHF number) is the pharmacy identity — siteNo is empty in the live catalogue;
  // trim: live data carries stray whitespace (e.g. "\t1085859") which would leak into docIds
  const practiceNo = (p.practiceNo ?? '').trim();
  return {
    id: practiceNo,
    name: (p.storeName ?? '').trim(),
    // phone when the catalogue carries it; else the pharmacy's BHF number ("name + BHF number" is
    // how EasyRx themselves identify a pharmacy). Label per EasyRx (Francois, 2026-07-06): they
    // recommend "Pharmacy Practice / BHF Number"; we use the single-term "Pharmacy Practice Number"
    // — familiar to pharmacies and unambiguous vs the doctor's own practice number.
    secondary: p.telephoneNo
      ? { label: 'Tel', value: p.telephoneNo }
      : practiceNo
        ? { label: 'Pharmacy Practice Number', value: practiceNo }
        : undefined,
    addressLine,
    province: p.province ?? undefined,
    town: p.town ?? undefined,
    isFavourite: !!p.isFavourite,
    searchText: joinParts([p.storeName, p.storeAddress, p.town, p.province, p.postalCode, p.telephoneNo, p.email]).toLowerCase(),
    raw: p,
  };
}

export function computAssistToUnified(p: ComputAssistPharmacy): UnifiedPharmacy {
  const addressLine = joinParts([p.address1, p.address2, p.address3, p.postal]);
  return {
    id: p.ramsno,
    name: p.storeName ?? '',
    secondary: p.ramsno ? { label: 'RAMS', value: p.ramsno } : undefined,
    addressLine,
    isFavourite: !!p.isFavourite,
    searchText: joinParts([p.storeName, p.address1, p.address2, p.address3, p.postal, p.ramsno]).toLowerCase(),
    raw: p,
  };
}
